import { useEffect, useMemo, useState } from 'react';
import type { AccountAnimal, AccountExplorer } from '../../shared/types/api';
import { CompassMark } from '../components/CompassMark';
import { ErrorNotice } from '../components/ErrorNotice';
import { LoadingTrail } from '../components/LoadingTrail';
import { TrailCompassArtwork } from '../components/TrailCompassArtwork';
import { ApiClientError, api } from '../lib/api';

const EXPLORER_BADGE_SYMBOLS: Record<AccountExplorer['badge'], string> = {
  sun: '☀',
  moon: '☾',
  star: '★',
  leaf: '❧',
  wave: '≈',
  mountain: '△',
};

function messageFrom(error: unknown): string {
  return error instanceof Error ? error.message : 'The trail book could not open yet.';
}

export function TrailBookPage({
  onOpenAnimal,
  onOpenGrownupArea,
  onShop,
  sessionValidated = false,
}: {
  onOpenAnimal: (entitlementId: string) => void;
  onOpenGrownupArea: () => void;
  onShop: () => void;
  sessionValidated?: boolean;
}) {
  const [animals, setAnimals] = useState<AccountAnimal[]>([]);
  const [explorers, setExplorers] = useState<AccountExplorer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    Promise.all([
      sessionValidated ? Promise.resolve(null) : api.getAccount(),
      api.listAccountAnimals(),
      api.listAccountExplorers(),
    ])
      .then(([, loadedAnimals, loadedExplorers]) => {
        if (!active) return;
        setAnimals(loadedAnimals);
        setExplorers(loadedExplorers);
      })
      .catch((reason: unknown) => {
        if (!active) return;
        if (reason instanceof ApiClientError && reason.status === 401) {
          onOpenGrownupArea();
          return;
        }
        setError(messageFrom(reason));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [onOpenGrownupArea, sessionValidated]);

  if (loading) return <LoadingTrail message="Opening your trail book…" />;
  return (
    <TrailBook
      animals={animals}
      explorers={explorers}
      error={error}
      onOpenAnimal={onOpenAnimal}
      onOpenGrownupArea={onOpenGrownupArea}
      onShop={onShop}
    />
  );
}

export function TrailBook({
  animals,
  explorers,
  error,
  onOpenAnimal,
  onOpenGrownupArea,
  onShop,
}: {
  animals: AccountAnimal[];
  explorers: AccountExplorer[];
  error: string | null;
  onOpenAnimal: (entitlementId: string) => void;
  onOpenGrownupArea: () => void;
  onShop: () => void;
}) {
  const [selectedExplorerId, setSelectedExplorerId] = useState(explorers[0]?.id ?? '');
  const activeExplorerId = explorers.some((explorer) => explorer.id === selectedExplorerId)
    ? selectedExplorerId
    : (explorers[0]?.id ?? '');
  const selectedExplorer = explorers.find((explorer) => explorer.id === activeExplorerId);
  const selectedAnimals = useMemo(
    () =>
      animals.filter(
        (animal) =>
          animal.unlocked &&
          Boolean(animal.entitlementId) &&
          (!activeExplorerId || animal.explorerId === activeExplorerId),
      ),
    [activeExplorerId, animals],
  );

  return (
    <div className="app-shell home-shell trail-book-shell">
      <header className="topbar">
        <a className="brand" href="/" aria-label="Wild Trails Kids home">
          <CompassMark />
          <span>Wild Trails Kids</span>
        </a>
        <div className="trail-book-topbar-actions">
          <button type="button" className="trail-book-shop-link" onClick={onShop}>
            <span aria-hidden="true">＋</span> Unlock another animal
          </button>
          <button type="button" className="parent-link" onClick={onOpenGrownupArea}>
            Grown-up area
          </button>
        </div>
      </header>

      <main>
        <section className="hero trail-book-hero">
          <div className="hero-copy">
            <p className="eyebrow">Your animal team</p>
            <h1>Where will their trails lead today?</h1>
            <p className="hero-intro">
              Pick an animal friend, open the map, and follow every new adventure.
            </p>
          </div>
          <TrailCompassArtwork />
        </section>

        {error && <ErrorNotice message={error} />}
        {explorers.length > 1 && (
          <nav className="trail-book-explorers" aria-label="Choose an explorer">
            {explorers.map((explorer) => (
              <button
                key={explorer.id}
                type="button"
                aria-pressed={explorer.id === activeExplorerId}
                className={explorer.id === activeExplorerId ? 'is-selected' : ''}
                onClick={() => setSelectedExplorerId(explorer.id)}
              >
                <span aria-hidden="true">{EXPLORER_BADGE_SYMBOLS[explorer.badge]}</span>
                {explorer.label}
              </button>
            ))}
          </nav>
        )}

        {selectedAnimals.length > 0 ? (
          <section
            className="animal-grid trail-book-grid"
            aria-label={`${selectedExplorer?.label ?? 'Your'} animal friends`}
          >
            {selectedAnimals.map((animal) => {
              const name = animal.animalName ?? animal.productName;
              return (
                <article key={animal.entitlementId} className="animal-card is-unlocked">
                  <div className="portrait-wrap">
                    <img
                      src={animal.portraitUrl ?? animal.previewImageUrl}
                      alt={`${name} portrait`}
                    />
                    {animal.explorerBadge && (
                      <span className="explorer-card-badge" aria-hidden="true">
                        {EXPLORER_BADGE_SYMBOLS[animal.explorerBadge]}
                      </span>
                    )}
                  </div>
                  <div className="animal-card-body">
                    <p className="species-label">{animal.explorerLabel}’s animal friend</p>
                    <h2>{name}</h2>
                    <p>{animal.personality ?? animal.productDescription}</p>
                    <button
                      type="button"
                      onClick={() => onOpenAnimal(animal.entitlementId ?? '')}
                      aria-label={`Follow ${name}'s trail`}
                    >
                      Open trail <span aria-hidden="true">→</span>
                    </button>
                  </div>
                </article>
              );
            })}
          </section>
        ) : (
          <section className="trail-book-empty" aria-labelledby="empty-trail-book-title">
            <span aria-hidden="true">✦</span>
            <h2 id="empty-trail-book-title">A new animal friend is waiting.</h2>
            <p>Ask a grown-up to add the code that came with your bracelet.</p>
            <button type="button" onClick={onOpenGrownupArea}>
              Find a grown-up
            </button>
          </section>
        )}
        {selectedExplorer && explorers.length > 1 && (
          <p className="family-footnote">Trail book for {selectedExplorer.label}</p>
        )}
      </main>
    </div>
  );
}
